<script lang="ts">
  import type { MenuData, MenuItem } from "../../lib/helpers/steps/menuItems";
  import { buildSocialGiftBundleMenu } from "../../lib/helpers/steps/menuItems";
  import ItemSelector from "../Menu/ItemSelector.svelte";
  import Button from "../Button.svelte";
  import Carousel from "../Carousel.svelte";
  import VerticalMenuItemCard from "../Menu/VerticalMenuItemCard.svelte";
  import HorizontalMenuItemCard from "../Menu/HorizontalMenuItemCard.svelte";
  import type { EmblaCarouselType } from "embla-carousel";
  import { getCopy } from "../../lib/stores/copy";
  import { saveSocialGiftingBundleChoices } from "../../lib/helpers/steps/menu";
  import {
    sessionStore,
    NavigationType,
    setSocialGiftBundleSelectedItemVariation
  } from "../../lib/stores/session";
  import { navigateTo } from "../../lib/routing";
  import { setupSelectedItems, isFullySpecified, isMenuFullySpecified } from "../../lib/helpers/steps/bundleSelection";
  import LoadingScreen from "../LoadingScreen.svelte";

  export let menuData: MenuData;
  export let isSaving = false;

  let isSelectorOpen = false;
  let emblaApi: EmblaCarouselType;
  let continueButton: { scrollIntoView: () => void; };
  let isComplete = false;
  let hasFullySpecifiedContinueRan = false;

  let selectedIndex: number;

  function closeSelector() {
    isSelectorOpen = false;
  }

  const mainButtonClick = async () => {
    try {
      isSaving = true;
      await saveSocialGiftingBundleChoices();
      navigateTo(NavigationType.Next);
    } catch (error) {
      console.error("Error saving social gifting bundle choices", error);
      // FIXME: Bugsnag? We should also show an error to the user
      isSaving = false;
    }
  };

  const handleVariationConfirmed = (index: number, event: CustomEvent<string>) => {
    setSocialGiftBundleSelectedItemVariation(index, event.detail);
    isSelectorOpen = false;
  };

  const handleMenuItemClick = (index: number, item: MenuItem) => {
    if (emblaApi) {
      emblaApi.scrollTo(menuItems.indexOf(item));
    }

    if (item.variations?.length > 1) {
      selectedIndex = index;
      isSelectorOpen = true;
    } else {
      setSocialGiftBundleSelectedItemVariation(index, item.variations[0].name);
    }
  };

  $: menuItems = buildSocialGiftBundleMenu(menuData);
  $: setupSelectedItems(menuItems);
  $: $sessionStore.socialGiftBundleSelectedItems, (() => isComplete = isFullySpecified(menuItems))();
  $: {
    // We check for the length, because we don't want anything to run if we don't actually have any items
    if (menuItems.length > 0 && isMenuFullySpecified(menuItems) && !hasFullySpecifiedContinueRan) {
      // If all items are fully specified to start with, I guess we should just store and move along...
      void mainButtonClick();
      hasFullySpecifiedContinueRan = true; // This is to avoid running this twice
    } else if (isComplete) {
      // The button might not exist when this code first runs
      continueButton?.scrollIntoView();
    }
  }
  $: copy = $getCopy("chooseItem") as Record<string, string>;
  $: selectedItems = $sessionStore.socialGiftBundleSelectedItems || [];
</script>

{#if isSelectorOpen}
  <ItemSelector
    on:close={closeSelector}
    item={menuItems[selectedIndex]}
    on:confirm={(event) => handleVariationConfirmed(selectedIndex, event)} />
{/if}

{#if isSaving}
  <LoadingScreen />
{:else}
  <div class="w-full mx-auto text-center lg:text-left">
    {#if menuItems}
      <div class="flex lg:hidden pt-4 content">
        <Carousel bind:emblaApi>
          {#each menuItems as item, index}
            {#if item.variations.length > 1}
              <div class="embla__slide">
                <div class="flex justify-center h-full">
                  <VerticalMenuItemCard
                    {item}
                    on:click={() => handleMenuItemClick(index, item)}
                    active={Boolean(selectedItems[index].variation)}
                    selectedVariation={selectedItems[index]?.variation} />
                </div>
              </div>
            {/if}
          {/each}
        </Carousel>
      </div>

      <div class="flex-col mt-8 gap-6 hidden lg:flex items-center">
        {#each menuItems as item, index}
          {#if item.variations.length > 1}
            <HorizontalMenuItemCard
              {item}
              on:click={() => handleMenuItemClick(index, item)}
              active={Boolean(selectedItems[index].variation)}
              selectedVariation={selectedItems[index]?.variation} />
          {/if}
        {/each}
      </div>
    {/if}
    <div class="flex w-full justify-center lg:justify-start mt-6 order-button lg:mt-12">
      <Button
        bind:this={continueButton}
        class="w-full lg:w-auto"
        primary
        on:click={mainButtonClick} disabled={!isComplete}>
        {copy.orderButtonCopy}
      </Button>
    </div>
  </div>
{/if}

<style>
  .embla__slide {
    flex: 0 0 var(--slide-size);
    min-width: 0;
    padding-left: var(--slide-spacing);
  }
</style>
